import { useEffect, useState } from 'react'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded'
import { listRaidHistory, subscribeRaidHistory, type ResolvedRaidRecord } from '../../services/raidHistory'
import styles from './Raid.module.css'

const formatWhen = (iso: string): string => {
  try {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    const diffMs = Date.now() - date.getTime()
    const day = 86_400_000
    if (diffMs < 60_000) return 'moments ago'
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`
    if (diffMs < day) return `${Math.floor(diffMs / 3_600_000)}h ago`
    if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

interface PastRaidsSectionProps {
  workspaceId: string | null
  compact?: boolean
  emptyMessage?: string
}

/**
 * Reads the client-side raid history for the workspace. Server-authoritative
 * history is Phase 2 scope; this ring buffer covers the "Past Raids" section
 * in the right pane (Active) and below the Forge form so users see continuity.
 */
export const PastRaidsSection = ({ workspaceId, compact = false, emptyMessage }: PastRaidsSectionProps) => {
  const [records, setRecords] = useState<ResolvedRaidRecord[]>(() => (workspaceId ? listRaidHistory(workspaceId) : []))

  useEffect(() => {
    if (!workspaceId) {
      setRecords([])
      return undefined
    }
    return subscribeRaidHistory(workspaceId, setRecords)
  }, [workspaceId])

  if (records.length === 0) {
    if (emptyMessage === undefined) return null
    return (
      <div className={styles.pastRaidsShell}>
        <h4 className={styles.pastRaidsTitle}>Past Raids</h4>
        <p className={styles.mutedNote}>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={styles.pastRaidsShell}>
      <h4 className={styles.pastRaidsTitle}>Past Raids</h4>
      <ul className={styles.pastRaidsList}>
        {records.slice(0, compact ? 3 : 6).map((record) => (
          <li key={record.raidId} className={styles.pastRaidsItem}>
            <span
              className={`${styles.pastRaidsBadge} ${record.outcome === 'VICTORY' ? styles.pastRaidsBadgeVictory : styles.pastRaidsBadgeFailure}`}
              aria-label={record.outcome === 'VICTORY' ? 'Victory' : 'Failure'}
            >
              {record.outcome === 'VICTORY'
                ? <EmojiEventsRoundedIcon fontSize="inherit" />
                : <ReportGmailerrorredRoundedIcon fontSize="inherit" />}
            </span>
            <div className={styles.pastRaidsBody}>
              <div className={styles.pastRaidsHead}>
                <strong className={styles.pastRaidsBossTitle}>{record.bossTitle}</strong>
                <span className={styles.pastRaidsWhen}>{formatWhen(record.resolvedAtIso)}</span>
              </div>
              <div className={styles.pastRaidsMeta}>
                {record.topContributor
                  ? `Top: ${record.topContributor.userDisplayName} · ${record.topContributor.damageDealt} DMG`
                  : 'No contributors'}
                {' · '}Pool {record.allocatedCreditPool}c
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
